import { InfiniteData, QueryKey, useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import {
    sendArtsDeleteEvent,
    sendArtsRenameEvent,
    sendArtsUploadEvent,
} from '~/adapters/services/accounts/arts/tracking';
import { EmptyFunction } from '~/adapters/typescript/emptyFunction';
import { useNotifications } from '~/contexts/NotificationContext';
import { Art } from './types';
import { GetArtProps, GetArtResponse, useArtsRepository } from './rest';

const infiniteArtsKey = 'infinite-arts';

export function useInfiniteArts(props: GetArtProps) {
    const { getArts } = useArtsRepository();

    return useInfiniteQuery([infiniteArtsKey, props], ({ pageParam }) => getArts({ pageParam, ...props }), {
        getNextPageParam: (lastData) => (lastData.hasMorePages ? lastData.currentPage + 1 : undefined),
        keepPreviousData: true,
    });
}

interface MutationOptions {
    onSettled: EmptyFunction;
}

export function useArtDelete({ onSettled }: MutationOptions) {
    const { pushApiErrorNotification } = useNotifications();
    const { deleteArt } = useArtsRepository();
    const client = useQueryClient();

    return useMutation(deleteArt, {
        onSettled,
        onSuccess: async () => {
            sendArtsDeleteEvent('Success');
            await client.invalidateQueries([infiniteArtsKey]);
        },
        onError: async () => {
            sendArtsDeleteEvent('Failure');
            pushApiErrorNotification('An error occurred while deleting art.');
        },
        onMutate: async () => {
            sendArtsDeleteEvent('Start');
        },
    });
}

export function useArtRename({ onSettled }: MutationOptions) {
    const { pushApiErrorNotification } = useNotifications();
    const { renameArt } = useArtsRepository();

    const client = useQueryClient();
    return useMutation(renameArt, {
        onSettled,
        onMutate: async (props) => {
            sendArtsRenameEvent('Start');
            const queries = client.getQueriesData<InfiniteData<GetArtResponse>>([infiniteArtsKey]);
            queries.forEach(([key, data]) => {
                const newData = {
                    ...data,
                    pages: data.pages.map((page: GetArtResponse) => {
                        return {
                            ...page,
                            items: page.items.map((item: Art) => {
                                if (item.id === props.uploadId) {
                                    return { ...item, name: props.name };
                                }
                                return item;
                            }),
                        };
                    }),
                };

                client.setQueryData(key, newData);
            });
            return queries;
        },
        onError: (_err, _variables, context: [QueryKey, InfiniteData<GetArtResponse>][] | undefined) => {
            sendArtsRenameEvent('Failure');
            context?.forEach(([key, data]) => {
                client.setQueryData(key, data);
            });
            pushApiErrorNotification('An error occurred while trying to rename art.');
        },
        onSuccess: async () => {
            sendArtsRenameEvent('Success');
            await client.invalidateQueries([infiniteArtsKey]);
        },
    });
}

export function useArtsUpload() {
    const { pushErrorNotification } = useNotifications();
    const { uploadArts } = useArtsRepository();
    const client = useQueryClient();

    return useMutation(uploadArts, {
        onMutate: async () => {
            sendArtsUploadEvent('Start');
            await client.cancelQueries([infiniteArtsKey]);
        },
        onError: (error: Error) => {
            sendArtsUploadEvent('Failure');
            pushErrorNotification(error.message);
        },
        onSuccess: async () => {
            sendArtsUploadEvent('Success');
            await client.invalidateQueries([infiniteArtsKey]);
        },
    });
}
